/**
 * VendorDetail page — shows vendor info with associated contracts, contacts,
 * compliance docs, risk alerts, and onboarding checklist.
 *
 * Requirements: 1.6, 1.7, 25.1, 25.2
 */

import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorService } from '@/services/vendorService';
import { invoiceService } from '@/services/invoiceService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ContactManager } from '@/components/ContactManager';
import { ComplianceTracker } from '@/components/ComplianceTracker';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import type { Vendor, VendorContact, Contract, ComplianceDocument, RiskAlert, InvoiceRecord } from '@/types';
import { UserRole } from '@/types';

const RISK_TIER_VARIANTS: Record<string, BadgeVariant> = {
  Critical: 'red',
  High: 'yellow',
  Medium: 'blue',
  Low: 'green',
};

const INVOICE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  'Pending Review': 'yellow',
  'Pending VP Approval': 'blue',
  Approved: 'green',
  Paid: 'green',
  Rejected: 'red',
  Disputed: 'red',
};

interface VendorDetailResponse {
  vendor: Vendor;
  contracts: Contract[];
  contacts: VendorContact[];
  complianceDocs: ComplianceDocument[];
  riskAlerts: RiskAlert[];
  onboardingChecklist: unknown[];
}

export function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, hasAnyRole } = useAuth();
  const vendorId = Number(id);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canWrite = user?.isAdmin ||
    user?.roles.some((r) =>
      ['Admin', 'Contract Specialist', 'Facilities Admin'].includes(r),
    );

  const canSeeFinancials = hasAnyRole([UserRole.Admin, UserRole.ContractSpecialist, UserRole.FacilitiesAdmin, UserRole.Accounting, UserRole.Controller, UserRole.CFO, UserRole.VPApprover]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => vendorService.getById(vendorId) as unknown as Promise<VendorDetailResponse>,
    enabled: !isNaN(vendorId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => vendorService.delete(vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      navigate('/vendors');
    },
  });

  const osintScanMutation = useMutation({
    mutationFn: () => vendorService.runOSINTScan(vendorId),
    onSuccess: () => {
      // Refresh vendor detail to show new risk alerts
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
  });

  const { data: invoiceData } = useQuery({
    queryKey: ['vendor-invoices', vendorId],
    queryFn: () => invoiceService.list({ vendorId, pageSize: 100 }),
    enabled: !isNaN(vendorId),
  });

  const vendorInvoices: InvoiceRecord[] = invoiceData?.data ?? [];

  if (isNaN(vendorId)) {
    return <ErrorMessage message="Invalid vendor ID." />;
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading vendor details…" />;
  }

  if (error || !data) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load vendor.'}
        onRetry={() => refetch()}
      />
    );
  }

  const { vendor, contracts, contacts, riskAlerts } = data;

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <Link
            to="/vendors"
            className="inline-flex items-center gap-1 text-slate-500 text-sm no-underline hover:text-baker-red transition-colors mb-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Vendors
          </Link>
          <h1 className="text-lg font-semibold text-text-primary m-0">{vendor.vendorName}</h1>
          <div className="flex gap-2 mt-2 items-center flex-wrap">
            <StatusBadge label={vendor.riskTier} variant={RISK_TIER_VARIANTS[vendor.riskTier] ?? 'gray'} />
            <StatusBadge label={vendor.departmentScope} variant="blue" />
            {!vendor.isActive && <StatusBadge label="Inactive" variant="red" />}
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/vendors/new?edit=${vendorId}`)}
              className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-600 cursor-pointer hover:bg-red-50 transition-all"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Vendor Info */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mt-0 mb-4">Vendor Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Category</span>
            <p className="mt-0.5 mb-0 text-slate-700">{vendor.vendorCategory ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Support Phone</span>
            <p className="mt-0.5 mb-0 text-slate-700">
              {vendor.supportPhone ? (
                <a href={`tel:${vendor.supportPhone.replace(/[^+\d]/g, '')}`} className="text-baker-red no-underline hover:underline font-medium">
                  {vendor.supportPhone}
                </a>
              ) : '—'}
            </p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Address</span>
            <p className="mt-0.5 mb-0 text-slate-700">{vendor.address ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">City</span>
            <p className="mt-0.5 mb-0 text-slate-700">{vendor.city ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">State</span>
            <p className="mt-0.5 mb-0 text-slate-700">{vendor.state ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Zip</span>
            <p className="mt-0.5 mb-0 text-slate-700">{vendor.zipCode ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Data Handling</span>
            <p className="mt-0.5 mb-0 text-slate-700">{vendor.dataHandlingClass ?? 'None'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Created</span>
            <p className="mt-0.5 mb-0 text-slate-700">{new Date(vendor.createdAt).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Updated</span>
            <p className="mt-0.5 mb-0 text-slate-700">{new Date(vendor.updatedAt).toLocaleDateString()}</p>
          </div>
        </div>
      </section>

      {/* Contacts */}
      <section className="mb-6">
        <ContactManager vendorId={vendorId} contacts={contacts} canWrite={!!canWrite} />
      </section>

      {/* Contracts */}
      {canSeeFinancials && (
      <section className="mb-6 bg-white rounded-lg border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-text-primary m-0">
            Contracts ({contracts.length})
          </h2>
        </div>
        {contracts.length === 0 ? (
          <p className="text-slate-400 text-sm px-5 py-6">No contracts associated with this vendor.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-transparent">
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Expiration</th>
                  <th className="text-right py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Value</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.contractId} className="border-b border-border-subtle hover:bg-page/80 transition-colors">
                    <td className="py-3 px-5">
                      <Link to={`/contracts/${c.contractId}`} className="text-baker-red no-underline font-medium hover:underline">
                        {c.contractTitle ?? '—'}
                      </Link>
                    </td>
                    <td className="py-3 px-5 text-slate-600">{c.contractType ?? '—'}</td>
                    <td className="py-3 px-5">
                      <StatusBadge label={c.lifecycleStatus ?? '—'} variant="gray" />
                    </td>
                    <td className="py-3 px-5 text-slate-600">
                      {c.expirationDate
                        ? new Date(c.expirationDate).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="py-3 px-5 text-right text-slate-700 font-medium tabular-nums">
                      {c.contractValue != null
                        ? `${Number(c.contractValue).toLocaleString()}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* Invoices */}
      {canSeeFinancials && (
      <section className="mb-6 bg-white rounded-lg border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-text-primary m-0">
            Invoices ({vendorInvoices.length})
          </h2>
        </div>
        {vendorInvoices.length === 0 ? (
          <p className="text-slate-400 text-sm px-5 py-6">No invoices associated with this vendor.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-transparent">
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="text-right py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {vendorInvoices.map((inv) => (
                  <tr key={inv.invoiceId} className="border-b border-border-subtle hover:bg-page/80 transition-colors">
                    <td className="py-3 px-5">
                      <Link to={`/invoices/${inv.invoiceId}`} className="text-baker-red no-underline font-medium hover:underline">
                        {inv.invoiceNumber ?? '—'}
                      </Link>
                    </td>
                    <td className="py-3 px-5 text-slate-600 tabular-nums">
                      {inv.invoiceDate
                        ? new Date(inv.invoiceDate).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="py-3 px-5 text-right text-slate-700 font-medium tabular-nums">
                      {inv.totalAmount != null
                        ? `$${Number(inv.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '—'}
                    </td>
                    <td className="py-3 px-5">
                      <StatusBadge
                        label={inv.lifecycleStatus}
                        variant={INVOICE_STATUS_VARIANTS[inv.lifecycleStatus] ?? 'gray'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* Compliance Tracker */}
      {canSeeFinancials && (
      <section className="mb-6">
        <ComplianceTracker vendorId={vendorId} canWrite={!!canWrite} />
      </section>
      )}

      {/* Risk Alerts */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base font-semibold text-text-primary m-0">
            Risk Alerts ({riskAlerts.length})
          </h2>
          {canWrite && (
            <button
              onClick={() => osintScanMutation.mutate()}
              disabled={osintScanMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-white text-text-secondary hover:bg-surface hover:text-text-primary transition-colors disabled:opacity-50"
            >
              {osintScanMutation.isPending ? 'Scanning…' : '🔍 Run OSINT Scan'}
            </button>
          )}
        </div>
        {osintScanMutation.isSuccess && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            Scan complete — {osintScanMutation.data?.totalSignals ?? 0} signal(s) found, {osintScanMutation.data?.alertsCreated ?? 0} new alert(s) created.
          </div>
        )}
        {osintScanMutation.isError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            Scan failed: {(osintScanMutation.error as Error)?.message ?? 'Unknown error'}
          </div>
        )}
        {riskAlerts.length === 0 ? (
          <p className="text-slate-400 text-sm">No risk alerts. Run an OSINT scan to check for vendor risk signals.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {riskAlerts.map((alert) => (
              <div
                key={alert.riskAlertId}
                className={`p-4 border rounded-lg transition-colors ${
                  alert.isResolved
                    ? 'border-border bg-page/50'
                    : 'border-amber-200 bg-amber-50/50'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm text-slate-700">
                    {alert.signalType ?? '—'}
                    <span className="ml-2 text-xs text-slate-400 font-normal">
                      via {alert.sourceName ?? 'Unknown'} · {alert.detectionDate ? new Date(alert.detectionDate).toLocaleDateString() : ''}
                    </span>
                  </span>
                  <StatusBadge
                    label={alert.severity ?? '—'}
                    variant={
                      alert.severity === 'High' ? 'red' :
                      alert.severity === 'Medium' ? 'yellow' : 'green'
                    }
                  />
                </div>
                <p className="mt-1.5 mb-0 text-sm text-slate-500 leading-relaxed">
                  {alert.summary ?? ''}
                </p>
                {alert.sourceUrl && (
                  <a href={alert.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-baker-red no-underline hover:underline mt-1 inline-block">
                    View source →
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Onboarding Checklist */}
      {canSeeFinancials && (
      <section className="mb-6">
        <OnboardingChecklist vendorId={vendorId} canWrite={!!canWrite} />
      </section>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Vendor"
        message={`Are you sure you want to delete "${vendor.vendorName}"? This will deactivate the vendor record.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
